import { AI_USER_ACTION, type AiUserToolCall } from '@/lib/api/ai-user/ai-user-api'
import { getScreenController, waitForScreenController } from './screen-bridge'

/**
 * Executa, na tela de edição da cobrança, as ações `billing_*` que o modelo pediu (ver
 * docs/ai-user.md, "Fase 2, etapa 2b"). A tela abre sozinha depois do último envio de um
 * grupo ou do envio com "dividir o pagamento"; aqui só se espera ela registrar.
 */

export const BILLING_ACTION_NAMES: readonly string[] = [
    AI_USER_ACTION.billingSetPayment,
    AI_USER_ACTION.billingSplit,
    AI_USER_ACTION.billingSave,
]

export function isBillingAction(name: string): boolean {
    return BILLING_ACTION_NAMES.includes(name)
}

/** Prazo para a tela da cobrança abrir depois do envio do agendamento. */
const OPEN_TIMEOUT_MS = 20_000

const PAYMENT_LABEL: Record<string, string> = {
    PIX: 'Pix',
    CREDIT_CARD: 'cartão de crédito',
    DEBIT_CARD: 'cartão de débito',
}

export async function executeBillingAction(
    call: AiUserToolCall,
    args: Record<string, unknown>,
    signal: AbortSignal,
): Promise<string> {
    const controller =
        getScreenController('billing') ??
        (await waitForScreenController('billing', () => true, OPEN_TIMEOUT_MS, signal))
    if (!controller) {
        return JSON.stringify({
            ok: false,
            messages: ['Erro: a tela da cobrança não está aberta. Ela abre depois do envio do último atendimento do grupo.'],
        })
    }
    switch (call.function.name) {
        case AI_USER_ACTION.billingSetPayment:
            return JSON.stringify(
                await controller.setPayment(
                    String(args.method ?? ''),
                    args.installments == null ? null : Number(args.installments),
                ),
            )
        case AI_USER_ACTION.billingSplit: {
            const parts = Array.isArray(args.parts) ? args.parts : []
            return JSON.stringify(
                await controller.setParts(
                    parts.map((part: any) => ({ method: String(part?.method ?? ''), amount: Number(part?.amount) })),
                ),
            )
        }
        case AI_USER_ACTION.billingSave:
            return JSON.stringify(await controller.save())
        default:
            return JSON.stringify({ ok: false, messages: [`Erro: ação desconhecida ${call.function.name}.`] })
    }
}

/** Uma linha para o Diário. */
export function describeBillingCall(name: string, args: Record<string, unknown>): string {
    switch (name) {
        case AI_USER_ACTION.billingSetPayment: {
            const label = PAYMENT_LABEL[String(args.method ?? '').toUpperCase()] || String(args.method ?? '')
            const count = Number(args.installments)
            return `Escolhendo na cobrança: ${label}${count > 1 ? ` em ${count}x` : ''}.`
        }
        case AI_USER_ACTION.billingSplit:
            return 'Dividindo o pagamento na cobrança.'
        case AI_USER_ACTION.billingSave:
            return 'Salvando a forma de pagamento da cobrança.'
        default:
            return name
    }
}
